import { apiClient } from './apiClient';
import { authService } from './authService';
import type {
  TrainerAssociation,
  ConfirmAssociationRequest,
  ConfirmAssociationResponse,
  FindAvailableTrainersRequest,
  FindAvailableTrainersResponse,
  UpdateTrainerPricingRequest,
  UpdateTrainerPermissionsRequest,
} from '../types/trainerAssociation';

export const trainerAssociationService = {
  /**
   * Get association details by token
   * Used on the confirmation page to show association info
   */
  async getAssociationByToken(token: string): Promise<TrainerAssociation> {
    return apiClient.get<TrainerAssociation>(
      `/trainer-profile/associations/by-token/${encodeURIComponent(token)}`
    );
  },

  /**
   * Confirm or reject an association
   */
  async confirmAssociation(
    data: ConfirmAssociationRequest
  ): Promise<ConfirmAssociationResponse> {
    return apiClient.post<ConfirmAssociationResponse>(
      '/trainer-profile/associations/confirm',
      data
    );
  },

  /**
   * Find available trainers for a business at specific date/time
   * @param businessProfileId - The business profile ID
   * @param data - Date and time slots to check availability
   */
  async findAvailableTrainers(
    businessProfileId: string,
    data: FindAvailableTrainersRequest
  ): Promise<FindAvailableTrainersResponse> {
    const token = authService.storage.getToken();
    return apiClient.post<FindAvailableTrainersResponse>(
      `/business-profile/${businessProfileId}/trainer-associations/available`,
      data,
      token || undefined
    );
  },

  // ===== Business-side Trainer Association Management =====

  /**
   * Get all trainer associations for a business profile
   */
  async getBusinessTrainerAssociations(
    businessProfileId: string,
    authToken: string,
    status?: string
  ): Promise<TrainerAssociation[]> {
    const url = status
      ? `/business-profile/${businessProfileId}/trainer-associations?status=${status}`
      : `/business-profile/${businessProfileId}/trainer-associations`;
    return apiClient.get<TrainerAssociation[]>(url, authToken);
  },

  /**
   * Update trainer pricing (hourly rate, VAT, gross)
   */
  async updateTrainerPricing(
    businessProfileId: string,
    associationId: string,
    data: UpdateTrainerPricingRequest,
    authToken: string
  ): Promise<TrainerAssociation> {
    return apiClient.put<TrainerAssociation>(
      `/business-profile/${businessProfileId}/trainer-associations/${associationId}/pricing`,
      data,
      authToken
    );
  },

  /**
   * Update trainer permissions
   */
  async updateTrainerPermissions(
    businessProfileId: string,
    associationId: string,
    data: UpdateTrainerPermissionsRequest,
    authToken: string
  ): Promise<TrainerAssociation> {
    return apiClient.put<TrainerAssociation>(
      `/business-profile/${businessProfileId}/trainer-associations/${associationId}/permissions`,
      data,
      authToken
    );
  },

  /**
   * Remove trainer association from business
   */
  async removeTrainerAssociation(
    businessProfileId: string,
    associationId: string,
    authToken: string
  ): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `/business-profile/${businessProfileId}/trainer-associations/${associationId}`,
      authToken
    );
  },
};
